/**
 * Hinweis im Angebots-Builder: Das Angebot ist nach der verbindlichen Annahme gesperrt.
 *
 * Der Server lehnt jede Änderung an Positionen/Varianten dann mit 409 `event_locked` ab
 * (`apps/api/src/lib/offer-lock.ts`). Statt den Betreiber erst beim Senden scheitern zu lassen,
 * sagt der Builder die Sperre vorher und nennt den Ausweg: seit der Nachtrag-Version
 * (docs/PLAN-NACHTRAG-VERSION.md, Einheit 4) ist das der Knopf „Nachtrag erstellen“ (nur Inhaber, Grund Pflicht).
 * Der frühere Verweis auf „Für Änderung öffnen“ entfällt hier.
 */
import { Button, Icon } from "./components";
import { NachtragErstellen } from "./NachtragBuilder";
import { FuerAenderungOeffnen, useHatGueltigeKue } from "./auftrag/FuerAenderungOeffnen";

/** Spiegel von `OFFER_LOCKED_STATUSES` (`apps/api/src/lib/offer-lock.ts`) — wortgleich halten.
 *  Der Server bleibt die Wahrheit (409 `event_locked`). */
export const OFFER_LOCKED_STATUSES: readonly string[] = ["offer_chosen", "paid", "payment_failed", "balance_due", "completed"];

export function istAngebotGesperrt(status: string | null | undefined): boolean {
  return status != null && OFFER_LOCKED_STATUSES.includes(status);
}

/** Kurzer Grund (Senden-Knopf, Fehlermeldung beim Versand). */
export const ANGEBOT_GESPERRT_KURZ = "Das Angebot wurde verbindlich angenommen und ist gesperrt. Zum Ändern zuerst freischalten (nur Inhaber): „Für Änderung öffnen“ bzw. „Nachtrag erstellen“.";

export function AngebotGesperrtHinweis({
  status,
  eventId,
  version = null,
  betragCents = null,
  istInhaber = false,
  onErstellt,
  onFreigeschaltet,
}: {
  status: string | null | undefined;
  eventId: string;
  /** Angenommene Fassung N (für „V1“). */
  version?: number | null;
  betragCents?: number | null;
  istInhaber?: boolean;
  /** Nach „Nachtrag erstellen“: die Builder-Seite lädt neu und wählt den Entwurf. */
  onErstellt?: (optionId: string | null) => void;
  /** Nach „Für Änderung öffnen“ (Weg mit gültiger Kostenübernahme): die Builder-Seite lädt neu und ist bearbeitbar. */
  onFreigeschaltet?: () => void;
}) {
  // Nachtrag-Version E5: mit gültiger unterschriebener Kostenübernahme (KÜ V2) ist die KÜ der Vertrag - dort kein Nachtrag per
  // Klick-Zustimmung, sondern der alte Weg „Für Änderung öffnen“ auf der Vorgangsseite (Server: 409 `gueltige_kue`).
  const { gueltig } = useHatGueltigeKue(istAngebotGesperrt(status) ? eventId : null);
  if (!istAngebotGesperrt(status)) return null;
  if (gueltig) {
    return (
      <div
        role="note"
        data-testid="builder-gesperrt-hinweis"
        className="mb-4 flex items-start gap-2.5 rounded-xl border border-outline-variant/50 bg-surface-container-low px-4 py-3 font-body-sm text-body-sm text-on-surface dark:border-outline/30 dark:bg-surface-variant/10"
      >
        <Icon name="lock" className="mt-0.5 shrink-0 text-[18px]" />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <p className="min-w-0 flex-1">
            Für diesen Auftrag gilt eine unterschriebene Kostenübernahme. Zum Ändern zuerst freischalten; danach holst du eine neue Kostenübernahme ein.
          </p>
          {istInhaber ? (
            <FuerAenderungOeffnen eventId={eventId} status={status ?? ""} knopf onGeoeffnet={onFreigeschaltet} />
          ) : (
            <p data-testid="freischalten-nur-inhaber" className="text-on-surface-variant dark:text-secondary-fixed-dim">
              Freischalten kann nur der Inhaber.
            </p>
          )}
        </div>
      </div>
    );
  }
  return <NachtragErstellen eventId={eventId} version={version} betragCents={betragCents} istInhaber={istInhaber} onErstellt={(id) => onErstellt?.(id)} />;
}

/**
 * Bugfund 02.11.2026 (Mimmo, Weihnachtsfeier zugesagt): die angenommene Fassung ist im Builder nur zum Lesen da. Statt
 * editierbarer Felder, die beim Autosave in 409 `event_locked` laufen („Nicht gespeichert“ + roter Fehler), steht über den
 * Positionen ruhig, wo Änderungen hingehören: in den Nachtrag-Entwurf (falls offen) bzw. zu „Nachtrag erstellen“ oben
 * (mit gültiger Kostenübernahme: „Für Änderung öffnen“).
 */
export function AngenommeneFassungNurLesen({
  eventId,
  version = null,
  entwurfVorhanden,
  onZumEntwurf,
}: {
  eventId: string;
  version?: number | null;
  entwurfVorhanden: boolean;
  onZumEntwurf?: () => void;
}) {
  const { gueltig } = useHatGueltigeKue(entwurfVorhanden ? null : eventId);
  const fassung = version != null ? `Fassung V${version}` : "Fassung";
  function zumFreischalten() {
    if (typeof document === "undefined") return;
    const ziel = document.querySelector<HTMLElement>('[data-testid="builder-gesperrt-hinweis"]');
    ziel?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    document.querySelector<HTMLElement>('[data-testid="nachtrag-grund"]')?.focus();
  }
  return (
    <div
      role="note"
      data-testid="builder-nur-lesen"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-outline-variant/50 bg-surface-container-low px-4 py-3 font-body-sm text-body-sm text-on-surface dark:border-outline/30 dark:bg-surface-variant/10 dark:text-inverse-on-surface"
    >
      <Icon name="visibility" className="shrink-0 text-[18px]" />
      <p className="min-w-0 flex-1">
        {entwurfVorhanden
          ? `Das ist die angenommene ${fassung} – nur zum Ansehen. Änderungen machst du im Nachtrag-Entwurf.`
          : gueltig
            ? `Das ist die angenommene ${fassung} – nur zum Ansehen. Zum Ändern oben „Für Änderung öffnen“.`
            : `Das ist die angenommene ${fassung} – nur zum Ansehen. Zum Ändern oben einen Nachtrag erstellen.`}
      </p>
      {entwurfVorhanden ? (
        <Button type="button" variant="secondary" className="!py-1.5" onClick={onZumEntwurf} data-testid="builder-nur-lesen-zum-entwurf">
          Zum Nachtrag-Entwurf
        </Button>
      ) : (
        <Button type="button" variant="secondary" className="!py-1.5" onClick={zumFreischalten} data-testid="builder-nur-lesen-freischalten">
          {gueltig ? "Zu „Für Änderung öffnen“" : "Zu „Nachtrag erstellen“"}
        </Button>
      )}
    </div>
  );
}

/**
 * Hinweiskarte im Senden-Dialog, solange das Angebot gesperrt ist (Antoine, 31.10.2026): statt der roten Fehlermeldung
 * „NICHT versendet“ steht hier ruhig, was zu tun ist, und der Knopf dazu. „Jetzt senden“ bleibt bis dahin aus.
 */
export function AngebotFreischaltenKarte({
  status,
  eventId,
  version = null,
  betragCents = null,
  istInhaber = false,
  onErstellt,
  onFreigeschaltet,
}: {
  status: string | null | undefined;
  eventId: string;
  version?: number | null;
  betragCents?: number | null;
  istInhaber?: boolean;
  onErstellt?: (optionId: string | null) => void;
  onFreigeschaltet?: () => void;
}) {
  const { gueltig } = useHatGueltigeKue(istAngebotGesperrt(status) ? eventId : null);
  if (!istAngebotGesperrt(status)) return null;
  return (
    <div data-testid="senden-freischalten-karte" className="rounded-xl border border-outline-variant/50 bg-surface-container-low px-4 py-3 font-body-sm text-body-sm text-on-surface dark:border-outline/30 dark:bg-surface-variant/10">
      <div className="flex items-start gap-2.5">
        <Icon name="lock" className="mt-0.5 shrink-0 text-[18px]" />
        <p>
          <strong className="font-bold">Dieses Angebot ist bereits angenommen.</strong> Zum Ändern und Senden zuerst freischalten.
        </p>
      </div>
      <div className="mt-3 pl-[28px]">
        {!istInhaber ? (
          <p data-testid="freischalten-nur-inhaber" className="text-on-surface-variant dark:text-secondary-fixed-dim">
            Freischalten kann nur der Inhaber.
          </p>
        ) : gueltig ? (
          <FuerAenderungOeffnen eventId={eventId} status={status ?? ""} knopf onGeoeffnet={onFreigeschaltet} />
        ) : (
          <NachtragErstellen kompakt eventId={eventId} version={version} betragCents={betragCents} istInhaber onErstellt={(id) => onErstellt?.(id)} />
        )}
      </div>
    </div>
  );
}
